import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Phone,
  Envelope,
  MapPin,
  NotePencil,
  Trash,
  Plus,
  Pencil,
  IdentificationCard,
  CaretDown,
  CaretRight,
  WhatsappLogo,
  ChatText,
  Receipt,
  CalendarBlank,
  CurrencyDollar,
  ChartBar,
  DotsThreeVertical,
} from "@phosphor-icons/react";
import ClientFormDialog from "@/components/ClientFormDialog";
import DocumentCard from "@/components/DocumentCard";
import DocumentFormDialog from "@/components/DocumentFormDialog";
import TransactionDialog from "@/components/TransactionDialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  PHONE_OPERATOR_LABELS,
  TX_TYPE_LABELS,
  TX_TYPE_COLORS,
  COUNTRY_FLAGS,
  daysUntilDay,
  formatCurrency,
  formatDate,
  whatsappUrl,
  smsUrl,
} from "@/lib/labels";

export default function ClientDetail({ clientId, onUpdated, onDeleted, onDocChanged }) {
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [addingDoc, setAddingDoc] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const [txs, setTxs] = useState([]);
  const [stats, setStats] = useState(null);
  const [addingTx, setAddingTx] = useState(false);
  const [editingTx, setEditingTx] = useState(null);
  const [confirmDeleteTx, setConfirmDeleteTx] = useState(null);
  const [historyType, setHistoryType] = useState(null); // string tx_type

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/clients/${clientId}`);
      setClient(data);
    } catch (e) {
      toast.error("No se pudo cargar el cliente");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  const loadTransactions = useCallback(async () => {
    try {
      const [{ data: rows }, { data: s }] = await Promise.all([
        api.get(`/clients/${clientId}/transactions`),
        api.get(`/clients/${clientId}/stats`),
      ]);
      setTxs(rows);
      setStats(s);
    } catch (e) {
      console.warn("Failed to load transactions/stats:", e);
    }
  }, [clientId]);

  useEffect(() => {
    load();
    loadTransactions();
  }, [load, loadTransactions]);

  const handleUpdated = (c) => {
    setClient((prev) => ({ ...prev, ...c }));
    setEditing(false);
    onUpdated && onUpdated(c);
    toast.success("Cliente actualizado");
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/clients/${clientId}`);
      setConfirmDelete(false);
      onDeleted && onDeleted(clientId);
      toast.success("Cliente eliminado");
    } catch (e) {
      toast.error("No se pudo eliminar");
    }
  };

  const onDocCreated = (doc) => {
    setClient((p) => ({ ...p, documents: [doc, ...(p?.documents || [])] }));
    setAddingDoc(false);
    onDocChanged && onDocChanged();
  };
  const onDocUpdated = (doc) => {
    setClient((p) => ({
      ...p,
      documents: (p?.documents || []).map((d) => (d.id === doc.id ? doc : d)),
    }));
    onDocChanged && onDocChanged();
  };
  const onDocDeleted = (docId) => {
    setClient((p) => ({
      ...p,
      documents: (p?.documents || []).filter((d) => d.id !== docId),
    }));
    onDocChanged && onDocChanged();
  };

  const onTxSaved = (tx) => {
    setAddingTx(false);
    setEditingTx(null);
    loadTransactions();
  };
  const handleDeleteTx = async () => {
    try {
      await api.delete(`/transactions/${confirmDeleteTx.id}`);
      setConfirmDeleteTx(null);
      toast.success("Movimiento eliminado");
      loadTransactions();
    } catch (e) {
      toast.error("No se pudo eliminar");
    }
  };

  if (loading && !client) {
    return (
      <div className="p-6 fade-up">
        <div className="h-8 w-72 bg-slate-200 animate-pulse" />
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="h-64 bg-white border border-slate-200 animate-pulse" />
          <div className="h-64 bg-white border border-slate-200 animate-pulse" />
        </div>
      </div>
    );
  }

  if (!client) return null;

  const hasExtraInfo = client.email || client.address || client.notes;
  const docs = client.documents || [];
  const operator = client.phone_operator;
  const day = client.phone_payment_day;
  const daysLeft = daysUntilDay(day);

  const reminderMessage = `Hola ${client.name}, te recuerdo que ${
    daysLeft === 0 ? "hoy" : daysLeft === 1 ? "mañana" : `en ${daysLeft} días`
  } es la fecha de pago de tu línea${
    operator ? ` ${PHONE_OPERATOR_LABELS[operator] || operator}` : ""
  }. ¿Pasas por la tienda o quieres que te ayude con el pago?`;
  const waUrl = whatsappUrl(client.phone, reminderMessage);
  const sms = smsUrl(client.phone, reminderMessage);

  return (
    <div className="fade-up" data-testid="client-detail">
      {/* Compact header */}
      <div className="bg-white border-b border-slate-200">
        <div className="px-6 py-5 flex items-start gap-4 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <h1
              className="font-display font-black text-3xl tracking-tight"
              data-testid="client-name"
            >
              {client.name}
            </h1>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-sm">
              {client.phone && (
                <a
                  href={`tel:${client.phone}`}
                  className="inline-flex items-center gap-1.5 font-mono-tab text-slate-700 hover:text-[#6B21A8]"
                  data-testid="client-phone"
                >
                  <Phone size={14} weight="bold" className="text-[#6B21A8]" /> {client.phone}
                </a>
              )}
              {client.phone && waUrl && (
                <a
                  href={waUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#25D366] hover:bg-[#1ebe5d] text-white text-[10px] font-bold uppercase tracking-wider transition-colors"
                  data-testid="whatsapp-button"
                  title="Enviar WhatsApp"
                >
                  <WhatsappLogo size={11} weight="bold" /> WhatsApp
                </a>
              )}
              {client.phone && sms && (
                <a
                  href={sms}
                  className="inline-flex items-center gap-1 px-2 py-0.5 border border-slate-900 text-[10px] font-bold uppercase tracking-wider btn-swap"
                  data-testid="sms-button"
                  title="Enviar SMS"
                >
                  <ChatText size={11} weight="bold" /> SMS
                </a>
              )}
              {client.birth_country && (
                <span className="brand-chip" data-testid="client-birth-country-chip">
                  <span className="text-sm leading-none">
                    {COUNTRY_FLAGS[client.birth_country] || "🌍"}
                  </span>
                  {client.birth_country}
                </span>
              )}
              {operator && (
                <span className="brand-chip" data-testid="client-operator-chip">
                  {PHONE_OPERATOR_LABELS[operator] || operator}
                </span>
              )}
              {day && (
                <span
                  className={daysLeft != null && daysLeft <= 3 ? "warn-chip" : "brand-chip"}
                  data-testid="client-payment-day-chip"
                >
                  <CalendarBlank size={11} weight="bold" />
                  Día {day}
                  {daysLeft != null && (
                    <span className="ml-1 opacity-80">
                      ·{" "}
                      {daysLeft === 0
                        ? "HOY"
                        : daysLeft === 1
                        ? "MAÑANA"
                        : `${daysLeft}D`}
                    </span>
                  )}
                </span>
              )}
            </div>
          </div>
          {/* Compact action group */}
          <div className="flex items-center gap-1.5 border border-slate-200 p-1 bg-slate-50">
            <button
              onClick={() => setAddingDoc(true)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-[#6B21A8] text-white text-[11px] font-bold uppercase tracking-wider hover:bg-[#581C87] transition-colors"
              data-testid="add-document-button"
              title="Agregar documento"
            >
              <Plus size={11} weight="bold" /> Doc
            </button>
            <button
              onClick={() => setAddingTx(true)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-900 text-white text-[11px] font-bold uppercase tracking-wider hover:bg-[#6B21A8] transition-colors"
              data-testid="add-tx-button"
              title="Registrar movimiento"
            >
              <Plus size={11} weight="bold" /> Mov
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger
                className="p-1.5 hover:bg-slate-200"
                data-testid="client-actions-menu"
                title="Más acciones"
              >
                <DotsThreeVertical size={16} weight="bold" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="rounded-none border-slate-900" align="end">
                <DropdownMenuItem
                  onClick={() => setEditing(true)}
                  className="rounded-none gap-2"
                  data-testid="edit-client-button"
                >
                  <Pencil size={14} weight="bold" /> Editar cliente
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setConfirmDelete(true)}
                  className="rounded-none gap-2 text-red-600 focus:text-red-700 focus:bg-red-50"
                  data-testid="delete-client-button"
                >
                  <Trash size={14} weight="bold" /> Eliminar cliente
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {hasExtraInfo && (
          <Collapsible open={showDetails} onOpenChange={setShowDetails}>
            <CollapsibleTrigger
              className="w-full px-6 py-2 border-t border-slate-100 flex items-center justify-between hover:bg-slate-50 text-left"
              data-testid="toggle-client-details"
            >
              <span className="label-eyebrow text-slate-500">
                {showDetails ? "Ocultar detalles" : "Ver más detalles"}
              </span>
              <CaretDown
                size={14}
                weight="bold"
                className={`text-slate-500 transition-transform ${
                  showDetails ? "rotate-180" : ""
                }`}
              />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="px-6 pb-4 space-y-2 text-sm">
                {client.email && (
                  <div className="inline-flex items-center gap-1.5" data-testid="client-email">
                    <Envelope size={14} weight="bold" className="text-[#6B21A8]" /> {client.email}
                  </div>
                )}
                {client.address && (
                  <div className="flex items-start gap-1.5" data-testid="client-address">
                    <MapPin size={14} weight="bold" className="text-[#6B21A8] mt-1 shrink-0" />
                    <span>{client.address}</span>
                  </div>
                )}
                {client.notes && (
                  <div className="border-l-2 border-[#6B21A8] pl-3 text-slate-700 flex items-start gap-2 mt-2">
                    <NotePencil size={14} weight="bold" className="text-[#6B21A8] mt-1 shrink-0" />
                    <p className="whitespace-pre-wrap" data-testid="client-notes">
                      {client.notes}
                    </p>
                  </div>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}
      </div>

      {/* Stats strip */}
      {stats && stats.total > 0 && (
        <div className="px-6 pt-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="stats-strip">
            <StatCard
              label="Total movimientos"
              value={stats.total}
              icon={<Receipt size={14} weight="bold" />}
            />
            <StatCard
              label="Ops por semana"
              value={stats.weekly_avg_count}
              hint="promedio últimos 90 días"
              icon={<ChartBar size={14} weight="bold" />}
            />
            <StatCard
              label="Monto por semana"
              value={formatCurrency(stats.weekly_avg_amount)}
              hint="promedio últimos 90 días"
              icon={<CurrencyDollar size={14} weight="bold" />}
            />
            <StatCard
              label="Cambios de cheque"
              value={stats.by_type?.cambio_cheque?.count || 0}
              icon={<Receipt size={14} weight="bold" />}
            />
          </div>
        </div>
      )}

      {/* Documents */}
      <div className="px-6 py-6">
        <div className="flex items-center gap-2 mb-3">
          <IdentificationCard size={16} weight="bold" />
          <span className="label-eyebrow">Documentos ({docs.length})</span>
        </div>
        {docs.length === 0 ? (
          <div className="border border-dashed border-slate-300 p-10 text-center bg-white" data-testid="no-documents">
            <IdentificationCard size={36} weight="thin" className="mx-auto text-slate-300" />
            <p className="mt-3 font-display font-bold text-base">Sin documentos aún</p>
            <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
              Cédula, pasaporte, credencial electoral o residencia.
            </p>
            <button
              onClick={() => setAddingDoc(true)}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-[#6B21A8] text-white text-xs font-bold uppercase tracking-wider btn-swap"
              data-testid="first-document-button"
            >
              <Plus size={14} weight="bold" /> Subir documento
            </button>
          </div>
        ) : (
          <div
            className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            data-testid="documents-grid"
          >
            {docs.map((d) => (
              <DocumentCard
                key={d.id}
                doc={d}
                onUpdated={onDocUpdated}
                onDeleted={onDocDeleted}
              />
            ))}
          </div>
        )}
      </div>

      {/* Transactions — grouped by type */}
      <div className="px-6 pb-10">
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Receipt size={16} weight="bold" />
            <span className="label-eyebrow">Movimientos ({txs.length})</span>
          </div>
          <button
            onClick={() => setAddingTx(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#6B21A8] text-white text-[11px] font-bold uppercase tracking-wider btn-swap"
            data-testid="add-tx-inline-button"
          >
            <Plus size={11} weight="bold" /> Movimiento
          </button>
        </div>
        {txs.length === 0 ? (
          <div
            className="border border-dashed border-slate-300 p-8 text-center bg-white"
            data-testid="no-transactions"
          >
            <Receipt size={32} weight="thin" className="mx-auto text-slate-300" />
            <p className="mt-2 text-sm text-slate-500">
              Registra cambios de cheque, envíos o recargas para empezar a ver el promedio.
            </p>
          </div>
        ) : (
          <TxGroupedList
            txs={txs}
            onOpenType={(type) => setHistoryType(type)}
          />
        )}
      </div>

      <TransactionHistoryDialog
        open={Boolean(historyType)}
        onOpenChange={(v) => !v && setHistoryType(null)}
        type={historyType}
        txs={historyType ? txs.filter((t) => t.tx_type === historyType) : []}
        onEdit={(t) => setEditingTx(t)}
        onDelete={(t) => setConfirmDeleteTx(t)}
        onAddNew={() => {
          setHistoryType(null);
          setAddingTx(true);
        }}
      />

      <ClientFormDialog open={editing} onOpenChange={setEditing} client={client} onSaved={handleUpdated} />
      <DocumentFormDialog
        open={addingDoc}
        onOpenChange={setAddingDoc}
        clientId={clientId}
        onSaved={onDocCreated}
      />
      <TransactionDialog
        open={addingTx}
        onOpenChange={setAddingTx}
        clientId={clientId}
        onSaved={onTxSaved}
      />
      <TransactionDialog
        open={Boolean(editingTx)}
        onOpenChange={(v) => !v && setEditingTx(null)}
        clientId={clientId}
        tx={editingTx}
        onSaved={onTxSaved}
      />

      {/* Mobile FABs — quick add for daily mobile workflow */}
      <button
        onClick={() => setAddingTx(true)}
        className="fab safe-bottom sm:hidden"
        style={{ bottom: "20px", right: "20px" }}
        title="Nuevo movimiento"
        data-testid="fab-new-tx"
      >
        <Receipt size={24} weight="bold" />
      </button>
      <button
        onClick={() => setAddingDoc(true)}
        className="fab fab-secondary safe-bottom sm:hidden"
        style={{ bottom: "20px", right: "94px", width: "52px", height: "52px" }}
        title="Nuevo documento"
        data-testid="fab-new-doc"
      >
        <IdentificationCard size={20} weight="bold" />
      </button>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent
          className="rounded-none border-slate-900"
          data-testid="confirm-delete-client-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Eliminar cliente</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción borra también sus documentos y movimientos. No se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="rounded-none bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-client-button"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={Boolean(confirmDeleteTx)}
        onOpenChange={(v) => !v && setConfirmDeleteTx(null)}
      >
        <AlertDialogContent
          className="rounded-none border-slate-900"
          data-testid="confirm-delete-tx-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Eliminar movimiento</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTx}
              className="rounded-none bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-tx-button"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StatCard({ label, value, hint, icon }) {
  return (
    <div className="border border-slate-200 bg-white p-3">
      <div className="flex items-center gap-1.5 text-slate-500">
        {icon}
        <span className="label-eyebrow text-[10px]">{label}</span>
      </div>
      <div className="font-display font-black text-xl tracking-tight mt-1 tabular-nums">
        {value}
      </div>
      {hint && <div className="text-[10px] text-slate-400 mt-0.5">{hint}</div>}
    </div>
  );
}

function TxGroupedList({ txs, onOpenType }) {
  // Group by tx_type and compute totals
  const groups = {};
  for (const t of txs) {
    const k = t.tx_type || "otro";
    if (!groups[k]) groups[k] = { count: 0, amount: 0, last: null };
    groups[k].count += 1;
    groups[k].amount += Number(t.amount || 0);
    if (!groups[k].last || new Date(t.date) > new Date(groups[k].last)) {
      groups[k].last = t.date;
    }
  }
  const entries = Object.entries(groups).sort((a, b) => b[1].amount - a[1].amount);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="tx-group-list">
      {entries.map(([type, g]) => {
        const color = TX_TYPE_COLORS[type] || TX_TYPE_COLORS.otro;
        return (
          <button
            key={type}
            onClick={() => onOpenType(type)}
            className="text-left bg-white border border-slate-200 hover:border-slate-900 transition-colors group"
            data-testid={`tx-group-${type}`}
          >
            <div
              className="px-3 py-2 flex items-center justify-between"
              style={{ background: color.bg, color: color.fg }}
            >
              <span className="text-[11px] font-bold uppercase tracking-wider">
                {TX_TYPE_LABELS[type] || type}
              </span>
              <CaretRight size={13} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </div>
            <div className="px-3 py-3 flex items-center justify-between">
              <div>
                <div className="font-display font-black text-2xl tabular-nums leading-none">
                  {g.count}
                </div>
                <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mt-1">
                  {g.count === 1 ? "operación" : "operaciones"}
                </div>
              </div>
              <div className="text-right">
                <div className="font-display font-black text-lg tabular-nums">
                  {formatCurrency(g.amount)}
                </div>
                <div className="text-[10px] text-slate-500 mt-1">
                  Último: {formatDate(g.last)}
                </div>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function TransactionHistoryDialog({ open, onOpenChange, type, txs, onEdit, onDelete, onAddNew }) {
  if (!type) return null;
  const color = TX_TYPE_COLORS[type] || TX_TYPE_COLORS.otro;
  const total = txs.reduce((sum, t) => sum + Number(t.amount || 0), 0);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="rounded-none border-slate-900 max-w-2xl max-h-[92vh] flex flex-col p-0 gap-0"
        data-testid="tx-history-dialog"
      >
        <DialogHeader className="px-6 pt-5 pb-3 space-y-2">
          <div className="label-eyebrow text-[#6B21A8]">·Historial</div>
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-3">
            <span
              className="text-[10px] font-bold uppercase tracking-wider px-2 py-1"
              style={{ background: color.bg, color: color.fg }}
            >
              {TX_TYPE_LABELS[type] || type}
            </span>
            <span>
              {txs.length} {txs.length === 1 ? "movimiento" : "movimientos"}
            </span>
          </DialogTitle>
          <DialogDescription className="flex items-center justify-between gap-2 flex-wrap">
            <span>
              Total acumulado: <strong className="font-mono-tab">{formatCurrency(total)}</strong>
            </span>
            <button
              onClick={onAddNew}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-[#6B21A8] text-white text-[11px] font-bold uppercase tracking-wider hover:bg-[#581C87] transition-colors"
              data-testid="tx-history-add-button"
            >
              <Plus size={11} weight="bold" /> Nuevo
            </button>
          </DialogDescription>
        </DialogHeader>

        <div className="border-t border-slate-200 overflow-y-auto flex-1">
          <div className="divide-y divide-slate-100">
            {txs.map((t) => (
              <div
                key={t.id}
                className="px-6 py-3 flex items-center gap-3"
                data-testid={`tx-history-row-${t.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-mono-tab text-xs text-slate-600">{formatDate(t.date)}</div>
                  {t.notes && (
                    <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{t.notes}</div>
                  )}
                </div>
                <div className="font-display font-black text-base tabular-nums shrink-0">
                  {formatCurrency(t.amount)}
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger
                    className="p-1 hover:bg-slate-100"
                    data-testid={`tx-history-menu-${t.id}`}
                  >
                    <DotsThreeVertical size={16} weight="bold" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="rounded-none border-slate-900" align="end">
                    <DropdownMenuItem
                      onClick={() => {
                        onOpenChange(false);
                        onEdit(t);
                      }}
                      className="rounded-none gap-2"
                      data-testid={`tx-history-edit-${t.id}`}
                    >
                      <Pencil size={14} weight="bold" /> Editar
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => {
                        onOpenChange(false);
                        onDelete(t);
                      }}
                      className="rounded-none gap-2 text-red-600 focus:text-red-700 focus:bg-red-50"
                      data-testid={`tx-history-delete-${t.id}`}
                    >
                      <Trash size={14} weight="bold" /> Eliminar
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
